/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useMemo } from "react";
import NewsItem from "./NewsItem";
import NewsDetail from "./NewsDetail";
import { Container, Row, Col, OverlayTrigger, Popover } from "react-bootstrap";
import NewsItemSkeleton from "./NewsItemSkeleton";
import NewsFeedSkeleton from "./NewsFeedSkeleton";
import { debounce } from "lodash";
import { handleNewsClick } from "./NewsFeedUtils";
import NewsDetailDiv from "./NewsDetailDiv";

const InfoPopover = (
  <Popover id="info-popover">
    <Popover.Body>
      <div>
        <i
          className="fa-solid fa-caret-up fa-lg me-2"
          style={{ color: "green" }}
        />{" "}
        High Relevance
      </div>
      <div>
        <i
          className="fa-solid fa-circle me-2 fa-sm"
          style={{ color: "#ffc107" }}
        />{" "}
        Moderate Relevance
      </div>
      <div>
        <i
          className="fa-solid fa-caret-down me-2 fa-lg"
          style={{ color: "red" }}
        />{" "}
        Low Relevance
      </div>
    </Popover.Body>
  </Popover>
);

const InfoButton = () => (
  <OverlayTrigger trigger={['hover', 'focus']} overlay={InfoPopover}>
    <i className="fa-solid fa-circle-info" />
  </OverlayTrigger>
);

function NewsFeed({
  newsList,
  isNewsLoading,
  noMoreNews,
  setDocumentIds,
  documentIds,
  handleLoadMoreNews,
  isMoreNewsLoading,
}) {
  const [selectedNews, setSelectedNews] = useState(null);
  const [prevDocumentIds, setPrevDocumentIds] = useState(null);
  const [apiCall, setApiCall] = useState(0);
  const rowRef = useRef(null);
  const newsArticles = useMemo(() => {
    setSelectedNews(null);
    const sortedNews = newsList.sort((a, b) => {
      const dateComparison =
        new Date(b.data_timestamp) - new Date(a.data_timestamp);
      if (
        dateComparison === 0 &&
        a.ner_relevance_score &&
        b.ner_relevance_score
      ) {
        return b.ner_relevance_score - a.ner_relevance_score;
      } else {
        return dateComparison;
      }
    });

    const allNullScores = sortedNews.every(
      (article) => article.ner_relevance_score === null
    );
    const allLowScores = sortedNews.every(
      (article) =>
        article.ner_relevance_score === null ||
        article.ner_relevance_score <= 0.2
    );
    if (allNullScores) {
      return sortedNews;
    } else if (allLowScores) {
      return []; 
    } else {
      return sortedNews; 
    }
  }, [newsList]);

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = rowRef.current;

    if (scrollTop + clientHeight >= scrollHeight && !selectedNews) {
      setApiCall((currentApiCall) => currentApiCall + 1);
    }
  };

  const debouncedHandleScroll = useRef(debounce(handleScroll, 300)).current;

  useEffect(() => {
    const currentRowRef = rowRef.current;
    if (currentRowRef) {
      currentRowRef.addEventListener("scroll", debouncedHandleScroll);
    }

    return () => {
      if (currentRowRef) {
        currentRowRef.removeEventListener("scroll", debouncedHandleScroll);
      }
    };
  }, [debouncedHandleScroll]);

  useEffect(() => {
    if (apiCall !== 0 && !noMoreNews && !selectedNews) {
      handleLoadMoreNews();
    }
  }, [apiCall]);

  const onClose = () => {
    setSelectedNews(null);
    setDocumentIds(prevDocumentIds);
  };

  return (
    <Container
      fluid
      style={{ height: "100%" }}
      className="shadow mt-2 border rounded-2"
    >
      <Row
        className="px-2 pt-2 border-bottom rounded-2 bg-light"
        style={{ height: "10%" }}
        key={"news-feed-title"}
      >
        <Col className="h-100">
          <div className="d-flex align-items-center text-dark-gray justify-content-between">
            <div className="news-feed-title h3">Info Flow</div>
            {newsArticles[0]?.ner_relevance_score && <InfoButton />}
          </div>
        </Col>
      </Row>
      <Row
        style={{ height: "88%", overflow: "auto" }}
        ref={rowRef}
        className="p-2"
        key={"news-feed-container"}
      >
        {isNewsLoading ? (
          <NewsFeedSkeleton />
        ) : selectedNews ? (
          <NewsDetailDiv news={selectedNews} onClose={onClose} />
        ) : (
          <>
            {newsArticles.map((news, index) => (
              <NewsItem
                news={news}
                key={`${news.news_url}-${index}`}
                onClick={() =>
                  handleNewsClick(
                    setSelectedNews,
                    news,
                    documentIds,
                    setPrevDocumentIds,
                    setDocumentIds
                  )
                }
              />
            ))}
            {isMoreNewsLoading && <NewsItemSkeleton />}
          </>
        )}
      </Row>
    </Container>
  );
}

export default NewsFeed;
